/**
 * Matriz de roles y permisos.
 *
 * El permiso es la unidad atomica; el rol es solo una agrupacion nombrada. Agregar
 * un rol futuro es agregar una entrada a `ROLE_PERMISSIONS` — no se reescribe nada.
 * La autorizacion real se aplica en el servidor (middleware por endpoint); la PWA
 * usa la misma matriz solo para ocultar/mostrar interfaz.
 */

import { DomainError } from './errors.js';

export const PERMISSIONS = [
  // Estudiantes y datos academicos (copia minima para cobros)
  'estudiantes:leer',
  // Pagos
  'pagos:registrar',
  'pagos:leer_propios',
  'pagos:leer_todos',
  'pagos:anular',
  // Recibos
  'recibos:emitir',
  'recibos:reimprimir',
  // Sincronizacion
  'sync:ejecutar',
  'sync:leer_resumen',
  // Conflictos
  'conflictos:leer',
  'conflictos:resolver',
  // Reportes y exportaciones
  'reportes:leer',
  'reportes:exportar',
  // Descargas ZIP
  'descargas:generar',
  'descargas:leer_historial',
  // Notificaciones
  'notificaciones:leer_propias',
  'notificaciones:leer_administrativas',
  'notificaciones:resolver_pendientes',
  // Administracion
  'usuarios:leer',
  'usuarios:administrar',
  'roles:asignar',
  'configuracion:leer',
  'configuracion:administrar',
  'auditoria:leer',
  'dispositivos:administrar',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ROLES = ['ADMINISTRADOR', 'CAJA'] as const;
export type Role = (typeof ROLES)[number];

const CAJA_PERMISSIONS: readonly Permission[] = [
  'estudiantes:leer',
  'pagos:registrar',
  'pagos:leer_propios',
  'recibos:emitir',
  'recibos:reimprimir',
  'sync:ejecutar',
  'sync:leer_resumen',
  'reportes:leer',
  'descargas:generar',
  'descargas:leer_historial',
  'notificaciones:leer_propias',
];

const ADMINISTRADOR_PERMISSIONS: readonly Permission[] = PERMISSIONS;

export const ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = Object.freeze({
  ADMINISTRADOR: ADMINISTRADOR_PERMISSIONS,
  CAJA: CAJA_PERMISSIONS,
});

export const ROLE_LABELS: Readonly<Record<Role, string>> = Object.freeze({
  ADMINISTRADOR: 'Administrador',
  CAJA: 'Pagos / Caja',
});

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value);
}

export function isPermission(value: unknown): value is Permission {
  return typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);
}

/** Permisos efectivos de un conjunto de roles (union). */
export function permissionsForRoles(roles: readonly Role[]): Set<Permission> {
  const result = new Set<Permission>();
  for (const role of roles) {
    for (const permission of ROLE_PERMISSIONS[role] ?? []) {
      result.add(permission);
    }
  }
  return result;
}

export function hasPermission(roles: readonly Role[], permission: Permission): boolean {
  return permissionsForRoles(roles).has(permission);
}

/**
 * Autorizacion completa: permiso + aislamiento por institucion.
 * Un usuario nunca puede tocar datos de otra institucion aunque tenga el permiso (evita IDOR).
 */
export interface Principal {
  readonly userId: string;
  readonly institutionId: string;
  readonly roles: readonly Role[];
  readonly installationId: string | null;
}

export function can(principal: Principal, permission: Permission): boolean {
  return hasPermission(principal.roles, permission);
}

export function authorize(
  principal: Principal,
  permission: Permission,
  scope?: { institutionId?: string },
): void {
  if (!can(principal, permission)) {
    throw new DomainError('PERMISO_DENEGADO', 'No tiene permiso para realizar esta accion', {
      permission,
    });
  }
  if (scope?.institutionId && scope.institutionId !== principal.institutionId) {
    // Mismo error que "sin permiso": no se revela la existencia del recurso ajeno.
    throw new DomainError('PERMISO_DENEGADO', 'No tiene permiso para realizar esta accion', {
      permission,
    });
  }
}

/**
 * `pagos:leer_todos` implica ver los pagos de cualquier usuario; con solo
 * `pagos:leer_propios` la consulta se restringe al propio usuario.
 */
export function paymentReadScope(principal: Principal): { onlyOwnUserId: string | null } {
  if (can(principal, 'pagos:leer_todos')) return { onlyOwnUserId: null };
  if (can(principal, 'pagos:leer_propios')) return { onlyOwnUserId: principal.userId };
  throw new DomainError('PERMISO_DENEGADO', 'No tiene permiso para consultar pagos', {
    permission: 'pagos:leer_propios',
  });
}
